import { CQA_QUALITY_ASSESSMENT_GRADING_SCREEN } from '../utils/isCQAGradingScreen';
import history from '../../history';

export function parseReturnToWorklistQuery(search: string): {
  projectId?: string;
  status?: string;
} {
  const params = new URLSearchParams(search);
  const projectId = params.get('projectId') ?? undefined;
  const status = params.get('status') ?? undefined;
  return { projectId, status };
}

/**
 * Augments cursorAPIContext for the CQA quality-assessment grading screen
 * opened via direct link (no navigation state). Ensures workitemId is set for submit and
 * builds returnToWorklistQuery (projectId/status) for "back to list" after submit/next.
 */
export function augmentCursorAPIContextForCQAGradingScreen(
  state: any,
  pathname: string,
  cursorAPIContext: any
): any {
  if (!pathname.startsWith(CQA_QUALITY_ASSESSMENT_GRADING_SCREEN)) {
    return cursorAPIContext;
  }

  let context = cursorAPIContext;

  // Ensure workitemId is set for PUT submit (from loaded invoices or URL path)
  const workitemId = context.workitemId;
  const hasWorkitemId = workitemId != null && String(workitemId).trim() !== '';
  if (!hasWorkitemId) {
    const sdCaseId = state?.gradeBenchPanel?.invoices?.[0]?.sdCaseId;
    if (sdCaseId != null && String(sdCaseId).trim() !== '') {
      context = { ...context, workitemId: sdCaseId };
    } else if (pathname) {
      const segments = pathname.split('/').filter(Boolean);
      const idFromPath = segments[segments.length - 1];
      if (idFromPath && idFromPath !== 'workItem') {
        context = { ...context, workitemId: idFromPath };
      }
    }
  }

  // Build returnToWorklistQuery (projectId/status) so submit/next/back can
  // return to the worklist with the project + status tab preserved. Prefer
  // the launch worklistContext; fall back to URL params / the loaded work
  // item for direct links and refreshes (CDMX-17399).
  const worklistContext = context.worklistContext as
    | { selectedProjectId?: string | number; selectedStatus?: string }
    | null
    | undefined;

  const returnToWorklistQuery: { projectId?: string; status?: string } = {};

  if (
    worklistContext?.selectedProjectId != null &&
    String(worklistContext.selectedProjectId).trim() !== ''
  ) {
    returnToWorklistQuery.projectId = String(worklistContext.selectedProjectId);
    if (worklistContext.selectedStatus) {
      returnToWorklistQuery.status = String(worklistContext.selectedStatus);
    }
  } else {
    const search =
      history?.location?.search ??
      (typeof window !== 'undefined' ? window.location.search : '');
    const parsed = search ? parseReturnToWorklistQuery(search) : {};
    if (parsed.projectId != null) {
      returnToWorklistQuery.projectId = parsed.projectId;
    }
    if (parsed.status != null) {
      returnToWorklistQuery.status = parsed.status;
    }
    const projectIdFromWorkItem =
      state?.gradeBenchPanel?.invoices?.[0]?.ruleCollectionId;
    if (
      returnToWorklistQuery.projectId == null &&
      projectIdFromWorkItem != null &&
      String(projectIdFromWorkItem).trim() !== ''
    ) {
      returnToWorklistQuery.projectId = String(projectIdFromWorkItem);
    }
  }

  if (Object.keys(returnToWorklistQuery).length > 0) {
    context = { ...context, returnToWorklistQuery };
  }

  return context;
}
